// @ts-strict-ignore
import get from 'lodash/get';
import setWith from 'lodash/setWith';
import isEmpty from 'lodash/isEmpty';

import {
  Columns,
  CommonDataTableFields,
  SegmentColumn,
  SegmentStats,
  SortTransformFunc,
  TableColumn,
  TableColumnType,
  TableRow,
} from 'common/components/data-table/types';
import {PropertyType} from 'common/report/constants';
import {parseDateSortKey, parseNumberRange, parseNumberRangeSortKey} from 'common/report/util/sorting/header-sorting';
import {flattenColumns, getNumSegmentColumns} from 'common/components/data-table/utils';
import {DEFAULT_TABLE_VIEW_N_LIMIT} from 'common/components/data-table/constants';
import {getIndexFromOrdinalLabel} from 'common/util';
import {ChartType, FlatSortConfig, SortBy, SortConfig, SortOrder} from 'common/types/reports/bookmark';

function rangeComparator(aVal, bVal) {
  const aRange = parseNumberRange(aVal);
  const bRange = parseNumberRange(bVal);
  if (aRange && bRange) {
    if (aRange[0] !== bRange[0]) {
      return aRange[0] - bRange[0];
    } else if (aRange[1] !== bRange[1]) {
      return aRange[1] - bRange[1];
    }
  }
  return null;
}

export function rowSortComparator(
  a: TableRow,
  b: TableRow,
  {
    sortOrder,
    sortTransform,
    sortField,
  }: {
    sortOrder: number;
    sortTransform?: SortTransformFunc<TableRow>;
    sortField?: string;
  },
): number {
  if (sortTransform) {
    return compareBySortTransform({a, b, sortTransform, field: sortField, sortOrder});
  }

  if (sortField) {
    const aVal = a[sortField]; // assume flat structure
    const bVal = b[sortField]; // assume flat structure
    const aType = typeof aVal;
    const bType = typeof bVal;

    if (aType === bType) {
      if (aType === `string`) {
        const rangeComparison = rangeComparator(aVal, bVal);
        if (rangeComparison !== null) {
          return sortOrder * rangeComparison;
        }
        return sortOrder * aVal.localeCompare(bVal);
      } else if (aType === `number` || aType === `boolean`) {
        return sortOrder * (aVal - bVal);
      }
    }

    // truthy values come before falsey values
    return (aVal ? 0 : 1) - (bVal ? 0 : 1);
  }

  return 0;
}

function _getSortTransform(col: TableColumn | null): SortTransformFunc {
  if (!col) {
    return null;
  } else if (col.sortTransform) {
    return col.sortTransform;
  } else if (col.type === TableColumnType.Segment) {
    const segmentType = col.contentType ?? PropertyType.String;
    return getSegmentSortTransform(segmentType, col.hasOrdinalPrefix);
  }
  return null;
}

// TODO: Move all sorting related code to live with SegmentFilterController
export function createRowCompareFunc({
  columns: columnsOrGroups,
  sortConfig,
  rows,
}: {
  columns: Columns;
  sortConfig: SortConfig;
  rows: TableRow[];
}): (a: TableRow, b: TableRow) => number {
  const columns = flattenColumns(columnsOrGroups);

  if (sortConfig.sortBy === SortBy.Value) {
    // do a single rowSortComparator sort (flat sort) as before
    const sortField = sortConfig.valueField;
    const sortTransform = _getSortTransform(columns.find((col) => col.field === sortField));

    const sortOrder = numericSortOrder(sortConfig.sortOrder);
    return (a, b) => rowSortComparator(a, b, {sortOrder, sortTransform, sortField});
  }
  const numSegments = getNumSegmentColumns(columns);
  if (numSegments < 2) {
    // if numSegments is 1 just do a flat sort
    const colSortConfig = sortConfig.colSortAttrs[0];
    // TODO(j) we should not allow SortBy.Value with 1 segment. dropdown should just be label sort or header click
    const sortField =
      colSortConfig.sortBy === SortBy.Value ? colSortConfig.valueField : `${CommonDataTableFields.SegmentPrefix}0`;
    const sortTransform = _getSortTransform(columns.find((col) => col.field === sortField));
    const sortOrder = numericSortOrder(colSortConfig.sortOrder);
    return (a, b) => rowSortComparator(a, b, {sortOrder, sortTransform, sortField});
  }
  return createMultiSegRowCompareFunc({columns, rows, sortConfig, numSegments});
}

export function createMultiSegRowCompareFunc({
  columns,
  rows,
  sortConfig,
  numSegments,
}: {
  columns: TableColumn[];
  sortConfig: SortConfig;
  rows: TableRow[];
  numSegments: number;
}) {
  const segmentStats = getSegmentStats({columns, rows, sortConfig, numSegments});

  return (a, b) => {
    // Compare rows at successive segmentation levels until comparison result !== 0
    const aPath = [];
    const bPath = [];
    const aStatsPath = [];
    const bStatsPath = [];
    let result = 0;
    for (let i = 0; i < numSegments; i++) {
      const segmentField = `${CommonDataTableFields.SegmentPrefix}${i}`;
      const aSegmentValue = a[segmentField];
      const bSegmentValue = b[segmentField];

      aPath.push(aSegmentValue);
      bPath.push(bSegmentValue);
      pushSegmentPath(aStatsPath, aSegmentValue, i === 0);
      pushSegmentPath(bStatsPath, bSegmentValue, i === 0);

      if (aSegmentValue === bSegmentValue) {
        // Nothing to sort when segments are the same, continue to next segmentation level
        continue;
      }

      const colSortConfig = sortConfig.colSortAttrs[i];
      const sortOrder = numericSortOrder(colSortConfig?.sortOrder);
      const labelCompare = !colSortConfig || colSortConfig.sortBy === SortBy.Label;
      const col = columns.find((column) => column.field === segmentField) as SegmentColumn;

      if (labelCompare) {
        const sortTransform = _getSortTransform(col);
        const rangeComparison = rangeComparator(aSegmentValue, bSegmentValue);
        result = sortTransform
          ? compareBySortTransform({a, b, sortTransform, field: segmentField, sortOrder})
          : rangeComparison !== null
            ? sortOrder * rangeComparison
            : sortOrder * aSegmentValue.localeCompare(bSegmentValue);
      } else {
        let aResult;
        let bResult;

        // bypass the default sortTransform generated by _getSortTransform,
        // value based sorting uses a default segment-label transform to break sorting ties only
        if (col.sortTransform) {
          aResult = col.sortTransform(a, segmentField, {sortOrder, colSortConfig, path: aPath.join(`,`)});
          bResult = col.sortTransform(b, segmentField, {sortOrder, colSortConfig, path: bPath.join(`,`)});
        } else {
          aResult = get(segmentStats, [...aStatsPath, `sum`]);
          bResult = get(segmentStats, [...bStatsPath, `sum`]);
        }

        result = sortOrder * (aResult - bResult);

        if (result === 0) {
          const rangeComparison = rangeComparator(aSegmentValue, bSegmentValue);

          // sort by label if the segments have same sum
          const sortTransform = getSegmentSortTransform(col.contentType, col.hasOrdinalPrefix);
          result = sortTransform
            ? compareBySortTransform({a, b, sortTransform, field: segmentField, sortOrder})
            : rangeComparison !== null
              ? sortOrder * rangeComparison
              : sortOrder * aSegmentValue.localeCompare(bSegmentValue);
        }
      }

      if (result !== 0) {
        break;
      }
    }
    return result;
  };
}

function getSegmentStats({
  columns,
  rows,
  sortConfig,
  numSegments,
}: {
  columns: TableColumn[];
  rows: TableRow[];
  sortConfig: SortConfig;
  numSegments: number;
}): SegmentStats {
  return rows.reduce((accum, row) => {
    const path = [];
    for (let i = 0; i < numSegments; i++) {
      const segmentValue = row[`${CommonDataTableFields.SegmentPrefix}${i}`];

      pushSegmentPath(path, segmentValue, i === 0);

      const colSortConfig = sortConfig.colSortAttrs[i];
      const {sortOrder} = colSortConfig;
      let value: number;
      if (colSortConfig.sortBy === SortBy.Value) {
        const {valueField} = colSortConfig;
        const sortTransform = columns.find((col) => col.field === valueField)?.sortTransform;
        value = sortTransform
          ? sortTransform(row, valueField, {sortOrder: numericSortOrder(sortOrder)})
          : row[valueField];
      } else {
        // no need to retrieve value for label sort-- the segment will be sorted alphabetically
        value = 0;
      }

      setWith(accum, [...path, `sum`], get(accum, [...path, `sum`], 0) + value, Object);
    }
    return accum;
  }, {});
}

function compareBySortTransform({
  a,
  b,
  sortTransform,
  field,
  sortOrder,
}: {
  a: TableRow;
  b: TableRow;
  sortTransform: SortTransformFunc;
  field: string;
  sortOrder: number;
}) {
  return sortOrder * (sortTransform(a, field, {sortOrder}) < sortTransform(b, field, {sortOrder}) ? -1 : 1);
}

/** Utility for starting / extending a path for use in a SegmentStats tree, e.g. ["Chrome", "children", "USA"] */
function pushSegmentPath(path: string[], segment: string, isFirst: boolean): void {
  if (!isFirst) {
    path.push(`children`);
  }
  path.push(segment);
}

export function numericSortOrder(sortOrder: SortOrder) {
  return sortOrder === SortOrder.Ascending ? 1 : -1;
}

function sanitizeTableSortField({
  valueField,
  numSegmentColumns,
  numMetricColumns,
}: {
  valueField: string;
  numSegmentColumns: number;
  numMetricColumns: number;
}) {
  if (valueField?.startsWith(CommonDataTableFields.SegmentPrefix)) {
    const segmentIndex = parseInt(valueField.slice(CommonDataTableFields.SegmentPrefix.length));
    if (segmentIndex >= numSegmentColumns) {
      // When removing a segment column, and valueField was set to sort on the last segment column, it'll now
      // be invalid and must be sanitized.
      valueField = `${CommonDataTableFields.SegmentPrefix}${numSegmentColumns - 1}`;
    }
  } else if (valueField?.startsWith(CommonDataTableFields.MetricPrefix) && numMetricColumns >= 1) {
    const metricIndex = parseInt(valueField.slice(CommonDataTableFields.MetricPrefix.length));
    if (metricIndex >= numMetricColumns) {
      // When removing a metric, and valueField was set to sort on the last metric column, it'll now
      // be invalid and must be sanitized.
      valueField = `${CommonDataTableFields.MetricPrefix}${numMetricColumns - 1}`;
    }
  }

  return valueField;
}

/**
 * Adds missing column sort configs in `sorting.colSortAttrs` and truncates `sorting.colSortAttrs` as needed so
 * its length matches the number of segments. This code is needed for when segment columns are added/removed.
 *
 * --
 * @todo @pablofierro this piece of code yields unwanted UX behavior where for instance: you have 2 breakdowns:
 * report_name and ARR. If you choose to sort `ARR` by numeric buckets A-Z and report_name by value,
 * then delete `report_name`, ARR buckets will now be sorted by value since this code simply removes `colSortAttrs`
 * from the end of the array, it has no concept of order of operations when adding/removing breakdowns
 */
export function sanitizeSortConfig({
  numSegmentColumns,
  numMetricColumns,
  sorting,
  canUseViewN,
  useDefaultViewNForNewSegments,
  sortingValueField,
}: {
  numSegmentColumns: number;
  numMetricColumns?: number;
  sorting: SortConfig;
  canUseViewN?: boolean;
  useDefaultViewNForNewSegments?: boolean;
  sortingValueField?: string;
}): SortConfig {
  const colSortAttrs: FlatSortConfig[] = new Array(numSegmentColumns);
  const defaultColSortValueField = sortingValueField ?? sorting.valueField;

  for (let i = 0; i < numSegmentColumns; i++) {
    if (sorting.colSortAttrs[i]) {
      colSortAttrs[i] = sorting.colSortAttrs[i];
    } else {
      let colSortAttr: FlatSortConfig;
      if (sorting.sortBy === SortBy.Value) {
        colSortAttr = {
          sortBy: SortBy.Value,
          sortOrder: SortOrder.Descending,
          valueField: defaultColSortValueField,
        };
      } else {
        // the next line duplicates the last column sorting config for any added segment
        colSortAttr = {
          ...(i === 0 ? {sortBy: SortBy.Value, sortOrder: SortOrder.Descending} : colSortAttrs[i - 1]),
        };
      }
      colSortAttrs[i] = {
        ...colSortAttr,
        ...(canUseViewN &&
          useDefaultViewNForNewSegments && {
            // If it's explicit query builder update that's adding new column, use default viewNLimit for the new
            // column.
            viewNLimit: DEFAULT_TABLE_VIEW_N_LIMIT,
          }),
      };
    }
  }

  return {
    ...sorting,
    valueField: sanitizeTableSortField({valueField: sorting.valueField, numMetricColumns, numSegmentColumns}),
    colSortAttrs: colSortAttrs.map((colSort) => {
      if (colSort.sortBy === SortBy.Value && colSort.valueField) {
        return {
          ...colSort,
          valueField: sanitizeTableSortField({
            valueField: colSort.valueField,
            numMetricColumns,
            numSegmentColumns,
          }),
        };
      }

      return colSort;
    }),
  };
}

/** Converts grouped (column) sort to the equivalent flat sort if possible */
export function flattenSingleSegSortConfig(sorting: SortConfig) {
  if (
    sorting.sortBy === SortBy.Column &&
    sorting.colSortAttrs.length === 1 &&
    sorting.colSortAttrs[0].sortBy === SortBy.Value
  ) {
    return {
      ...sorting.colSortAttrs[0],
      colSortAttrs: sorting.colSortAttrs,
    };
  }
  return sorting;
}

/** Returns valueField only if this is a SortByValueConfig (flat sort), otherwise null.
 * We need this because bar chart uses 'valueField' on SortByColumnsConfig as "value to sort secondarily on" */
export function getPrimaryValueField(sorting: SortConfig): string | null {
  return sorting.sortBy === SortBy.Value ? sorting.valueField : null;
}

/** Attempts to update `sorting` to column sort (aka grouped sort) if newly multiseg, or to an equivalent flat sort (aka
 * value sort) if newly single seg.
 *
 * Always sanitizes sorting (i.e. makes colSortAttrs.length equal number of segments)
 * */
export function updateSortingForNewNumSegments({
  numSegmentColumns,
  numMetricColumns,
  oldNumSegmentColumns,
  valueFieldIsGroupSortable,
  sorting,
  canUseViewN,
  useDefaultViewNForNewSegments,
  sortingValueField,
}: {
  numSegmentColumns: number;
  numMetricColumns?: number;
  oldNumSegmentColumns: number;
  valueFieldIsGroupSortable: boolean;
  sorting: SortConfig;
  canUseViewN?: boolean;
  useDefaultViewNForNewSegments?: boolean;
  /** provides an override to the default value field populated to any new colSOrtAttrs */
  sortingValueField?: string;
}): SortConfig {
  const newlyMultiSeg = numSegmentColumns === 2 && oldNumSegmentColumns === 1;
  const newlySingleSeg = numSegmentColumns === 1 && oldNumSegmentColumns === 2;
  const shouldSwitchToGroupSorting = newlyMultiSeg && sorting.sortBy === SortBy.Value && valueFieldIsGroupSortable;
  const defaultColSortValueField = sortingValueField ?? sorting.valueField;

  if (shouldSwitchToGroupSorting) {
    return {
      sortBy: SortBy.Column,
      colSortAttrs: Array.from({length: numSegmentColumns}, (_, segmentIdx) => {
        return {
          sortBy: SortBy.Value,
          sortOrder: sorting.sortOrder,
          valueField: defaultColSortValueField,
          ...(canUseViewN && {
            viewNLimit:
              segmentIdx === 0 && sorting.viewNLimit !== undefined
                ? // carry over flat sort's viewNLimit to the first column of grouped sort when going from 1 -> 2 breakdowns
                  sorting.viewNLimit
                : DEFAULT_TABLE_VIEW_N_LIMIT,
          }),
        };
      }),
    };
  } else if (newlySingleSeg) {
    const sanitizedSorting = sanitizeSortConfig({
      numSegmentColumns,
      numMetricColumns,
      sorting,
      canUseViewN,
      useDefaultViewNForNewSegments,
    });
    return flattenSingleSegSortConfig(sanitizedSorting);
  }
  return sanitizeSortConfig({
    numSegmentColumns: numSegmentColumns || 1,
    numMetricColumns,
    sorting,
    canUseViewN,
    useDefaultViewNForNewSegments,
  });
}

/**
 * Generate default `SortConfig` based on the specified inputs. If `flatSortConfig` param is passed, that will be used
 * as the basis for the generated `SortConfig`.
 */
export function getDefaultSortConfig({
  numSegmentColumns,
  valueSortField,
  flatSortConfig,
  allowGroupSort = false,
  canUseViewN,
}: {
  numSegmentColumns: number;
  valueSortField: string;
  flatSortConfig?: FlatSortConfig;
  allowGroupSort?: boolean;
  canUseViewN?: boolean;
}): SortConfig {
  if (isEmpty(flatSortConfig)) {
    flatSortConfig = {
      sortBy: SortBy.Value,
      sortOrder: SortOrder.Descending,
      valueField: valueSortField,
      ...(canUseViewN && {viewNLimit: DEFAULT_TABLE_VIEW_N_LIMIT}),
    };
  }

  const colSortAttrs = Array.from({length: numSegmentColumns}, () => ({
    sortBy: flatSortConfig.sortBy,
    sortOrder: flatSortConfig.sortOrder,
    ...(flatSortConfig.sortBy === SortBy.Value ? {valueField: valueSortField} : {}),
    ...(canUseViewN && {viewNLimit: DEFAULT_TABLE_VIEW_N_LIMIT}),
  }));

  if (flatSortConfig.sortBy === SortBy.Label) {
    return {sortBy: SortBy.Column, colSortAttrs};
  }
  if (allowGroupSort && numSegmentColumns > 1) {
    return {
      sortBy: SortBy.Column,
      colSortAttrs,
    };
  }
  return {
    sortBy: SortBy.Value,
    sortOrder: flatSortConfig.sortOrder,
    valueField: flatSortConfig.valueField || valueSortField,
    ...(canUseViewN && {viewNLimit: DEFAULT_TABLE_VIEW_N_LIMIT}),
    colSortAttrs,
  };
}

/**
 * @param hasOrdinalPrefix if the segment should be sorted by alpha label, e.g. A., B., C., ... BA.
 */
export function getSegmentSortTransform(propertyType: PropertyType, hasOrdinalPrefix: boolean): SortTransformFunc {
  if (propertyType === PropertyType.Datetime) {
    return (row: TableRow, field) => {
      return parseDateSortKey(row[field]);
    };
  } else if (propertyType === PropertyType.Number) {
    return (row: TableRow, field, {sortOrder}) => {
      return parseNumberRangeSortKey(row[field], {sortOrder});
    };
  } else if (hasOrdinalPrefix && propertyType === PropertyType.String) {
    // if this is the labeled (first segment), we sort by the ordinal label where A. through Z. comes before BA., etc.
    return (row: TableRow, field) => {
      return getIndexFromOrdinalLabel(row[field]);
    };
  }
  return null;
}

export function getChartTypeForSorting(chartType: ChartType): ChartType {
  return chartType === ChartType.Column ? ChartType.Line : chartType;
}
